import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MdKeyboardArrowDown } from "react-icons/md";

import { buildGroupedObservationHistoryItems } from "helpers/articleLocationObservationRecords";
import { dateToMySQLFormat, formatObservationValue, formatUser } from "helpers";
import { getObservationStatusLabel } from "helpers/articleLocation";
import {
  ArticleLocationObservationRecordStatus,
  ArticleLocationObservationStatus,
  ObservationRecordHistoryEntry,
} from "types";

export type ObservationRecordHistoryItem = ObservationRecordHistoryEntry;

export type ObservationRecordItem = {
  id: string;
  title: string;
  quantity: number;
  status: ArticleLocationObservationRecordStatus;
  observation: string | null;
  history: ObservationRecordHistoryItem[];
};

export type ObservationRecordSavePayload = {
  recordId: string;
  observation: string;
  quantityMode: "total" | "specific";
  specificQuantity: number | null;
};

type ObservationDraftState = {
  observation: string;
  quantityMode: "total" | "specific";
  specificQuantity: string;
};

interface ObservationRecordsModalProps {
  open: boolean;
  loading?: boolean;
  savingRecordId?: string | null;
  readOnly?: boolean;
  isRecordEditable?: (record: ObservationRecordItem) => boolean;
  title?: string;
  subtitle?: string | null;
  records: ObservationRecordItem[];
  onClose: () => void;
  onSaveRecord?: (
    payload: ObservationRecordSavePayload,
  ) => void | Promise<void>;
}

const getRecordStatusLabel = (
  status: ObservationRecordItem["status"],
) => {
  if (status === "in_work_orders") {
    return "En OT";
  }

  if (status === "reserved") {
    return "Mercaderí­a reservada";
  }

  if (status === "discharged") {
    return "Mercadería egresada";
  }

  if (status === "transferred") {
    return "Mercadería transferida";
  }

  return getObservationStatusLabel(status);
};

const formatHistoryCreatedAt = (
  value?: string | number | Date,
) => {
  if (!value) return "-";

  return dateToMySQLFormat(value as string | Date) || "-";
};

const buildInitialDraft = (record: ObservationRecordItem): ObservationDraftState => ({
  observation: record.observation ?? "",
  quantityMode: "total",
  specificQuantity: "",
});

export default function ObservationRecordsModal({
  open,
  loading = false,
  savingRecordId = null,
  readOnly = false,
  isRecordEditable,
  title = "Observaciones",
  subtitle = null,
  records,
  onClose,
  onSaveRecord,
}: ObservationRecordsModalProps) {
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, ObservationDraftState>>({});

  useEffect(() => {
    if (!open) {
      setEditingRecordId(null);
      setDrafts({});
    }
  }, [open]);

  const canEditRecord = (record: ObservationRecordItem) =>
    !readOnly && record.status !== "in_work_orders" &&
    (isRecordEditable ? isRecordEditable(record) : true);

  const handleStartEdit = (record: ObservationRecordItem) => {
    if (!canEditRecord(record)) return;

    setEditingRecordId(record.id);
    setDrafts((prev) => ({
      ...prev,
      [record.id]: prev[record.id] ?? buildInitialDraft(record),
    }));
  };

  const handleCancelEdit = (recordId: string) => {
    setEditingRecordId((current) => (current === recordId ? null : current));
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
  };

  const handleDraftChange = (
    recordId: string,
    field: keyof ObservationDraftState,
    value: string,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [recordId]: {
        ...(prev[recordId] ?? {
          observation: "",
          quantityMode: "total",
          specificQuantity: "",
        }),
        [field]: value,
      },
    }));
  };

  const handleSave = async (recordId: string) => {
    const record = records.find((item) => item.id === recordId);
    if (!onSaveRecord || !record || !canEditRecord(record)) {
      return;
    }

    const draft = drafts[recordId];
    if (!draft) {
      return;
    }

    await onSaveRecord({
      recordId,
      observation: draft.observation,
      quantityMode: draft.quantityMode,
      specificQuantity:
        draft.quantityMode === "specific" && draft.specificQuantity
          ? Number(draft.specificQuantity)
          : null,
    });

    setEditingRecordId((current) => (current === recordId ? null : current));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}

          {loading ? (
            <Box py={4}>
              <Typography variant="body2" color="text.secondary" align="center">
                Cargando observaciones...
              </Typography>
            </Box>
          ) : records.length === 0 ? (
            <Box py={2}>
              <Typography variant="body2">
                No hay observaciones para mostrar.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {records.map((record) => {
                const recordIsEditable = canEditRecord(record);
                const isEditing = recordIsEditable && editingRecordId === record.id;
                const draft = drafts[record.id] ?? buildInitialDraft(record);
                const groupedHistory = buildGroupedObservationHistoryItems(
                  record.history,
                );
                const specificQuantityValue = Number(draft.specificQuantity || 0);
                const exceedsQuantity =
                  draft.quantityMode === "specific" &&
                  specificQuantityValue > record.quantity;
                const helperText =
                  draft.quantityMode === "specific"
                    ? `Máximo disponible para esta observación: ${record.quantity}`
                    : `Se aplicará a la cantidad total: ${record.quantity}`;

                return (
                  <Paper key={record.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Box p={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={2}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={600}>
                              {formatObservationValue(record.observation)}
                            </Typography>
                            <Chip
                              size="small"
                              label={getRecordStatusLabel(record.status)}
                              color={
                                record.status === "not_available"
                                  ? "warning"
                                  : record.status === "reserved"
                                    ? "secondary"
                                    : record.status === "transferred" || record.status === "in_work_orders"
                                      ? "info"
                                    : "success"
                              }
                              variant="outlined"
                            />
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            Cantidad asociada: {record.quantity}
                          </Typography>
                        </Stack>

                        <Button
                          variant={isEditing ? "outlined" : "contained"}
                          disabled={!onSaveRecord || !recordIsEditable}
                          onClick={() =>
                            isEditing
                              ? handleCancelEdit(record.id)
                              : handleStartEdit(record)
                          }
                        >
                          {isEditing
                            ? "Cancelar edición"
                            : recordIsEditable
                              ? "Editar"
                              : "Solo lectura"}
                        </Button>
                      </Stack>

                      {isEditing && recordIsEditable ? (
                        <Box mt={2}>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Observación"
                                fullWidth
                                multiline
                                minRows={3}
                                value={draft.observation}
                                onChange={(event) =>
                                  handleDraftChange(
                                    record.id,
                                    "observation",
                                    event.target.value,
                                  )
                                }
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Alcance de la edición
                              </Typography>
                              <RadioGroup
                                value={draft.quantityMode}
                                onChange={(event) =>
                                  handleDraftChange(
                                    record.id,
                                    "quantityMode",
                                    event.target.value,
                                  )
                                }
                                sx={{ width: "100%" }}
                              >
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  alignItems={{ xs: "flex-start", md: "center" }}
                                  justifyContent={{ xs: "flex-start", md: "space-between" }}
                                  spacing={2}
                                  sx={{ width: "100%" }}
                                >
                                  <FormControlLabel
                                    value="total"
                                    control={<Radio />}
                                    label={`Cantidad total (${record.quantity})`}
                                    sx={{ m: 0 }}
                                  />
                                  <Box
                                    sx={{
                                      display: { xs: "none", md: "block" },
                                      width: 320,
                                      visibility: "hidden",
                                    }}
                                  />
                                </Stack>
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  alignItems={{ xs: "flex-start", md: "center" }}
                                  justifyContent={{ xs: "flex-start", md: "space-between" }}
                                  spacing={2}
                                  sx={{ width: "100%" }}
                                >
                                  <FormControlLabel
                                    value="specific"
                                    control={<Radio />}
                                    label="Cantidad específica"
                                    sx={{ m: 0 }}
                                  />
                                  <TextField
                                    label="Cantidad específica"
                                    type="number"
                                    size="small"
                                    disabled={draft.quantityMode !== "specific"}
                                    value={draft.specificQuantity}
                                    onChange={(event) =>
                                      handleDraftChange(
                                        record.id,
                                        "specificQuantity",
                                        event.target.value,
                                      )
                                    }
                                    error={exceedsQuantity}
                                    helperText={
                                      exceedsQuantity
                                        ? "La cantidad no puede superar el total del registro."
                                        : helperText
                                    }
                                    inputProps={{ min: 1, max: record.quantity }}
                                    sx={{
                                      width: { xs: "100%", md: 320 },
                                      ml: { xs: 0, md: "auto" },
                                    }}
                                  />
                                </Stack>
                              </RadioGroup>
                            </Grid>

                            <Grid item xs={12}>
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  variant="outlined"
                                  onClick={() => handleCancelEdit(record.id)}
                                >
                                  Descartar
                                </Button>
                                <Button
                                  variant="contained"
                                  disabled={
                                    !onSaveRecord ||
                                    savingRecordId === record.id ||
                                    (draft.quantityMode === "specific" &&
                                      (!draft.specificQuantity ||
                                        specificQuantityValue <= 0 ||
                                        exceedsQuantity))
                                  }
                                  onClick={() => void handleSave(record.id)}
                                >
                                  Guardar
                                </Button>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                      ) : null}
                    </Box>

                    <Accordion disableGutters>
                    <AccordionSummary expandIcon={<MdKeyboardArrowDown />}>
                        <Typography variant="body2" fontWeight={600}>
                          Historial ({groupedHistory.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {groupedHistory.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No hay historial para este registro.
                          </Typography>
                        ) : (
                          <Stack spacing={1.5}>
                            {groupedHistory.map((historyItem) => (
                              <Paper
                                key={historyItem.group_key}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 1.5 }}
                              >
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatHistoryCreatedAt(historyItem.created_at)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Usuario:</strong> {formatUser(historyItem)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Observación:</strong>{" "}
                                    {formatObservationValue(
                                      historyItem.new_observation,
                                    )}
                                  </Typography>
                                  {historyItem.grouped_count > 1 ? (
                                    <Typography variant="body2">
                                      <strong>Cantidad:</strong>{" "}
                                      {historyItem.grouped_count}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
